/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { createContext, ReactNode, useContext, useMemo, useRef, useState, SyntheticEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isStrEmptyOrNull,
  isValidEmailAddress,
  isValidPassword,
  isValidReenterField,
} from '../common/UtilityFunctions';
import useAbortController from '../common/hooks/useAbortController';
import axiosCall from '../services/axios';

type statusAlertType = {
  status: string;
  message: string;
};

type createAccountValue = {
  status: string;
  statusAlert: statusAlertType;
  setStatusAlert: (statusAlertType) => void;
  loadingMessage: string;
  handleClick: () => void;
  handleSuccess: () => void;
  handleError: (string) => void;
  handleSubmit: () => Promise<boolean>;
  validateFields: (SynetheticEvent) => void;

  createAccountModalOpen: boolean;
  setCreateAccountModalOpen: (boolean) => void;
  emailAddressError: boolean;
  setEmailAddressError: (boolean) => void;
  emailAddressErrorMsg: string;
  setEmailAddressErrorMsg: (string) => void;
  reenterEmailAddressError: boolean;
  setReenterEmailAddressError: (boolean) => void;
  reenterEmailAddressErrorMsg: string;
  setReenterEmailAddressErrorMsg: (string) => void;
  passwordError: boolean;
  setPasswordError: (boolean) => void;
  passwordErrorMsg: string;
  setPasswordErrorMsg: (string) => void;
  reenterPasswordError: boolean;
  setReenterPasswordError: (boolean) => void;
  reenterPasswordErrorMsg: string;
  setReenterPasswordErrorMsg: (string) => void;

  reenterEmailAddressChanged: boolean;
  setReenterEmailAddressChanged: (boolean) => void;
  reenterPasswordChanged: boolean;
  setReenterPasswordChanged: (boolean) => void;
  passwordFocus: boolean;
  setPasswordFocus: (boolean) => void;
  showPassword: boolean;
  setShowPassword: (boolean) => void;
  handleClickShowPassword: () => void;
  handleMouseDownPassword: () => void;
  showReenterPassword: boolean;
  setShowReenterPassword: (boolean) => void;
  handleClickShowReenterPassword: () => void;
  handleMouseDownReenterPassword: () => void;

  emailAddress: string;
  setEmailAddress: (string) => void;
  reenterEmailAddress: string;
  setReenterEmailAddress: (string) => void;
  password: string;
  handleUpdatePassword: (string) => void;
  reenterPassword: string;
  handleUpdateReenterPassword: (string) => void;
  certified: boolean;
  setCertified: (boolean) => void;
};

const CreateAccountContext = createContext<createAccountValue | null>(null);

const useCreateAccountContext = () => {
  const createAccountContext = useContext(CreateAccountContext);
  if (createAccountContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('createAccountContext must be inside a CreateAccountProvider');
  }
  return createAccountContext;
};

type createAccountProviderProps = {
  children: ReactNode;
};

const CreateAccountProvider = ({ children }: createAccountProviderProps): JSX.Element => {
  const [submitStatus, setSubmitStatus] = useState('done');
  const [statusAlert, setStatusAlert] = useState({
    status: '',
    message: '',
  });
  const messageRef = useRef('');

  const [loadingMessage, setLoadingMessage] = useState('');
  const [createAccountModalOpen, setCreateAccountModalOpen] = useState(false);
  const [emailAddressError, setEmailAddressError] = useState(false);
  const [emailAddressErrorMsg, setEmailAddressErrorMsg] = useState('');
  const [reenterEmailAddressError, setReenterEmailAddressError] = useState(false);
  const [reenterEmailAddressErrorMsg, setReenterEmailAddressErrorMsg] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMsg, setPasswordErrorMsg] = useState('');
  const [reenterPasswordError, setReenterPasswordError] = useState(false);
  const [reenterPasswordErrorMsg, setReenterPasswordErrorMsg] = useState('');

  const [reenterEmailAddressChanged, setReenterEmailAddressChanged] = useState(false);
  const [reenterPasswordChanged, setReenterPasswordChanged] = useState(false);
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [reenterEmailAddress, setReenterEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [reenterPassword, setReenterPassword] = useState('');
  const [certified, setCertified] = useState(false);

  const handleClick = () => setCertified(!certified);

  const status = useMemo(() => {
    const statuses = [submitStatus];
    if (statuses.includes('error')) {
      return 'error';
    }
    if (statuses.includes('loading')) {
      return 'loading';
    }
    return 'done';
  }, [submitStatus]);

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const handleClickShowReenterPassword = () => setShowReenterPassword(!showReenterPassword);

  const handleMouseDownReenterPassword = () => setShowReenterPassword(!showReenterPassword);

  const handleUpdatePassword = (newPassword: string) => {
    setPassword(() => {
      if (newPassword.length > 20) {
        setStatusAlert({
          status: 'error',
          message: 'Password is too long. Max 20 characters.',
        });
      } else {
        setStatusAlert({
          status: '',
          message: '',
        });
      }
      return newPassword.substring(0, 20);
    });
  };

  const handleUpdateReenterPassword = (newPassword: string) => {
    setReenterPassword(() => {
      if (newPassword.length > 20) {
        setStatusAlert({
          status: 'error',
          message: 'Reenter Password is too long. Max 20 characters.',
        });
      } else {
        setStatusAlert({
          status: '',
          message: '',
        });
      }
      return newPassword.substring(0, 20);
    });
  };

  // API-related section
  const { reset } = useAbortController();
  const createAccountUrl = process.env.REACT_APP_PUBLIC_WEB_USER_CREATE;
  const emailErrorMessage =
    'We’re unable to locate an account with the email address you entered. Please verify the email address or contact our Customer Service team at customerservice@swmc.com or (800) 345-7884 for help.';

  const createAccount = useCallback(async () => {
    let success = false;
    if (!emailAddress) {
      messageRef.current = 'Email is not provided.';
      return success;
    } else if (!password) {
      messageRef.current = 'Password is not provided.';
      return success;
    }

    const controller = reset();
    const config = { signal: controller.signal };

    const formData = {
      email: emailAddress,
      password: password,
    };

    try {
      const response = await axiosCall({ url: createAccountUrl, method: 'post', data: formData }, config, {
        'Content-Type': 'application/json',
      });
      if (response.success) {
        success = true;
        messageRef.current = 'Borrower account created!';
      } else {
        messageRef.current = response.error;
      }
    } catch (error) {
      console.error('Unable to create a new Borrower account: ' + error);
      messageRef.current = emailErrorMessage;
    }

    return success;
  }, [messageRef, emailAddress, password, createAccountUrl, emailErrorMessage, reset]);

  // end API-related section

  const navigate = useNavigate();

  const handleSuccess = useCallback(() => {
    setSubmitStatus('loading');
    setLoadingMessage('Redirecting to Login...');
    setStatusAlert({ status: 'success', message: 'Successfully created account! You will be redirected shortly.' });
    setTimeout(() => {
      navigate('/login');
    }, 3000);
  }, [navigate]);

  const handleError = useCallback((type: string) => {
    setSubmitStatus('error');
    setLoadingMessage('');
    switch (type) {
      case 'This Web User account already exists.': {
        setStatusAlert({
          status: 'error',
          message: 'An account for the provided email already exists. Please try logging in.',
        });
        break;
      }
      default:
        setStatusAlert({
          status: 'error',
          message: emailErrorMessage,
        });
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitStatus('loading');
    setLoadingMessage('Creating Account...');
    let success: boolean;
    try {
      success = await createAccount();

      if (success) {
        handleSuccess();
      } else {
        // output the error in console for STS
        console.error(messageRef.current);
        handleError(messageRef.current);
      }
    } catch (e) {
      // output the error in console for STS
      console.error(e);
      handleError('other');
    }
    return success;
  }, [messageRef, handleSuccess, handleError, createAccount]);

  const validateFields = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();
      let isError = false;

      if (!isValidEmailAddress(emailAddress)) {
        setEmailAddressError(true);
        setEmailAddressErrorMsg(
          isStrEmptyOrNull(emailAddress) ? 'Please enter your Email Address.' : 'Email Address provided is not valid.',
        );
        isError = true;
      } else {
        setEmailAddressError(false);
        setEmailAddressErrorMsg(' ');
      }

      if (!isValidReenterField(emailAddress, reenterEmailAddress)) {
        setReenterEmailAddressError(true);
        setReenterEmailAddressErrorMsg(
          isStrEmptyOrNull(reenterEmailAddress)
            ? 'Please re-enter your Email Address.'
            : 'Email Addresses do not match.',
        );
        isError = true;
      } else {
        setReenterEmailAddressError(false);
        setReenterEmailAddressErrorMsg(' ');
      }

      if (!isValidPassword(password)) {
        setPasswordError(true);
        setPasswordErrorMsg(
          isStrEmptyOrNull(password) ? 'Please enter your Password.' : 'Password does not meet criteria.',
        );
        isError = true;
      } else {
        setPasswordError(false);
        setPasswordErrorMsg(' ');
      }

      if (!isValidReenterField(password, reenterPassword)) {
        setReenterPasswordError(true);
        setReenterPasswordErrorMsg(
          isStrEmptyOrNull(reenterPassword) ? 'Please re-enter your Password.' : 'Passwords do not match.',
        );
        isError = true;
      } else {
        setReenterPasswordError(false);
        setReenterPasswordErrorMsg(' ');
      }

      if (!certified) isError = true;

      if (isError) {
        return false;
      } else {
        // No Validation Errors, call handleSubmit()
        const success: boolean = await handleSubmit();
        return success;
      }
    },
    [emailAddress, reenterEmailAddress, password, reenterPassword, certified, handleSubmit, handleError],
  );

  return (
    <CreateAccountContext.Provider
      value={{
        status,
        statusAlert,
        setStatusAlert,
        loadingMessage,
        handleClick,
        handleSuccess,
        handleError,
        handleSubmit,
        validateFields,

        createAccountModalOpen,
        setCreateAccountModalOpen,
        emailAddressError,
        setEmailAddressError,
        emailAddressErrorMsg,
        setEmailAddressErrorMsg,
        reenterEmailAddressError,
        setReenterEmailAddressError,
        reenterEmailAddressErrorMsg,
        setReenterEmailAddressErrorMsg,
        passwordError,
        setPasswordError,
        passwordErrorMsg,
        setPasswordErrorMsg,
        reenterPasswordError,
        setReenterPasswordError,
        reenterPasswordErrorMsg,
        setReenterPasswordErrorMsg,

        reenterEmailAddressChanged,
        setReenterEmailAddressChanged,
        reenterPasswordChanged,
        setReenterPasswordChanged,
        passwordFocus,
        setPasswordFocus,
        showPassword,
        setShowPassword,
        handleClickShowPassword,
        handleMouseDownPassword,
        showReenterPassword,
        setShowReenterPassword,
        handleClickShowReenterPassword,
        handleMouseDownReenterPassword,

        emailAddress,
        setEmailAddress,
        reenterEmailAddress,
        setReenterEmailAddress,
        password,
        handleUpdatePassword,
        reenterPassword,
        handleUpdateReenterPassword,
        certified,
        setCertified,
      }}
    >
      {children}
    </CreateAccountContext.Provider>
  );
};

export { CreateAccountProvider, useCreateAccountContext };
